import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { ToastProvider, useToast } from '../components/Toast'
import { useDb, getRoleHomePath, getSessionUser } from '../state/DbProvider'
import { buildCallbackRedirect, hasSignupCallbackParams } from '../utils/authCallback'

function LoginInner() {
  const { db, actions } = useDb()
  const toast = useToast()
  const location = useLocation()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const already = db ? getSessionUser(db) : null
  const hasSignupCallback = hasSignupCallbackParams(location)

  useEffect(() => {
    if (!hasSignupCallback) return
    navigate(buildCallbackRedirect('/join/complete', location), { replace: true })
  }, [hasSignupCallback, location, navigate])

  useEffect(() => {
    if (hasSignupCallback) return
    if (already) navigate(getRoleHomePath(already), { replace: true })
  }, [already, hasSignupCallback, navigate])

  if (hasSignupCallback || already) return null

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 sm:py-10">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-start">
        <Card className="overflow-hidden border-slate-300 shadow-lg shadow-slate-200/70">
          <div className="bg-gradient-to-br from-slate-50 via-white to-slate-100 px-5 py-5">
            <span className="inline-flex rounded-full bg-[var(--jnavi-navy)] px-3 py-1 text-xs font-semibold text-white">
              初めてご利用の方へ
            </span>

            <div className="mt-4 grid gap-3">
              <div>
                <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">
                  まずは代理店申請をお願いします
                </h1>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  初回ログインには、代理店申請と所属総代理店または運営の承認後のパスワード設定が必要です。承認前はログインできません。
                </p>
              </div>

              <Link
                to="/agency-signup"
                aria-label="代理店申請"
                className="block rounded-xl bg-[var(--jnavi-navy)] px-5 py-4 text-white shadow-sm transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--jnavi-navy)] focus:ring-offset-2"
              >
                <span className="block text-sm font-medium text-slate-200">はじめての方はこちら</span>
                <span className="mt-1 block text-lg font-semibold">代理店申請</span>
                <span className="mt-1 block text-sm text-slate-200">
                  申請後、承認メールからパスワード設定へ進みます
                </span>
              </Link>
            </div>
          </div>

          <div className="grid gap-4 border-t border-slate-100 px-5 py-5">
            <div>
              <div className="text-sm font-semibold text-slate-900">登録手順</div>
              <p className="mt-1 text-sm text-slate-500">
                申請からログインまでは、次の3ステップです。
              </p>
            </div>

            <ol className="grid gap-3">
              <li className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-[auto_1fr]">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                  1
                </div>
                <div className="grid gap-1">
                  <div className="text-sm font-semibold text-slate-900">代理店申請を行う</div>
                  <p className="text-sm leading-6 text-slate-600">
                    初めてご利用の方は、上の代理店申請ボタンから必要情報を送信してください。
                  </p>
                </div>
              </li>

              <li className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-[auto_1fr]">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                  2
                </div>
                <div className="grid gap-1">
                  <div className="text-sm font-semibold text-slate-900">
                    承認メールからパスワードを設定する
                  </div>
                  <p className="text-sm leading-6 text-slate-600">
                    所属総代理店または運営の承認後、
                    <span className="mx-1 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[13px] text-slate-700">
                      noreply@j-connect.link
                    </span>
                    から届くメール内のリンクでパスワードを設定してください。
                  </p>
                </div>
              </li>

              <li className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-[auto_1fr]">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                  3
                </div>
                <div className="grid gap-1">
                  <div className="text-sm font-semibold text-slate-900">ログインする</div>
                  <p className="text-sm leading-6 text-slate-600">
                    このログインページで、登録したメールアドレスと設定済みのパスワードを入力してログインしてください。
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </Card>

        <Card
          title="ログイン"
          subtitle="承認済みの方は、設定済みのメールアドレスとパスワードでログインしてください。"
        >
          <div className="grid gap-4 px-5 py-4">
            <label className="grid gap-1 text-sm">
              <span className="text-slate-700">メールアドレス</span>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                placeholder="you@example.com"
                type="email"
              />
            </label>

            <label className="grid gap-1 text-sm">
              <span className="text-slate-700">パスワード</span>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                placeholder="••••••••"
                type="password"
              />
            </label>

            <Button
              variant="primary"
              className="w-full"
              onClick={async () => {
                const res = await actions.login(email, password)
                if (!res.ok) {
                  toast.show(res.message ?? 'ログインに失敗しました', 'error')
                  return
                }
                navigate('/')
              }}
            >
              ログイン
            </Button>

            <Link to="/forgot" className="text-sm text-blue-600 hover:underline">
              パスワードを忘れた
            </Link>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              コネクター登録は代理店または総代理店から共有された招待URLからのみ受け付けています。
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default function LoginPage() {
  return (
    <ToastProvider>
      <LoginInner />
    </ToastProvider>
  )
}
